import { useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { formatPhone } from '../../lib/phone';
import PasswordResetCard from '../shared/PasswordResetCard';

function statusLabel(status) {
    return String(status || 'invited').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function DriverApplicationPortal({ user, theme = 'light', mode = 'apply' }) {
    const dark = theme === 'dark';
    const [applicant, setApplicant] = useState(null);
    const [steps, setSteps] = useState([]);
    const [onboardingRecord, setOnboardingRecord] = useState(null);
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState('');
    const [validationToken, setValidationToken] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const [verified, setVerified] = useState(false);
    const verificationRequired = !verified && (user?.app_metadata?.verification_required === true
        || user?.user_metadata?.force_password_reset === true);

    async function loadApplication() {
        setLoading(true);
        setMessage('');
        const { data, error } = await supabaseClient.functions.invoke('driver-application-auth', {
            body: { action: 'lookup_application', markStarted: mode === 'apply' }
        });
        if (error || data?.ok === false) {
            setMessage(error?.message || data?.error || 'No driver application invite was found for this login.');
            setApplicant(null);
            setSteps([]);
            setOnboardingRecord(null);
        } else {
            setApplicant(data.applicant || null);
            setSteps(data.steps || []);
            setOnboardingRecord(data.onboardingRecord || null);
        }
        setLoading(false);
    }

    useEffect(() => {
        if (verificationRequired) setLoading(false);
        else loadApplication();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, verificationRequired]);

    async function invoke(action, body = {}) {
        setWorking(true);
        setMessage('');
        const { data, error } = await supabaseClient.functions.invoke('driver-application-auth', {
            body: { action, ...body }
        });
        setWorking(false);
        if (error || data?.ok === false) {
            throw new Error(error?.message || data?.error || 'Request failed.');
        }
        return data;
    }

    async function sendCode() {
        try {
            await invoke('send_code', { applicantId: applicant?.id });
            setCodeSent(true);
            setMessage('We sent a one-time code by SMS to the mobile number on your invite.');
        } catch (error) {
            setMessage(error.message);
        }
    }

    async function verifyCode() {
        try {
            const data = await invoke('verify_code', { applicantId: applicant?.id, code });
            setValidationToken(data.validationToken || '');
            setMessage('Phone validated. You can now start the application.');
        } catch (error) {
            setMessage(error.message);
        }
    }

    async function startApplication() {
        try {
            await invoke('start_application', { applicantId: applicant?.id, validationToken });
            setMessage('Application started. Your hiring lead has been notified.');
            await loadApplication();
        } catch (error) {
            setMessage(error.message);
        }
    }

    if (loading) return <div className="p-10 text-center text-gray-500">Loading driver application…</div>;
    if (verificationRequired) return <PasswordResetCard user={user} onDone={() => setVerified(true)} />;

    return (
        <div className={`min-h-full p-6 ${dark ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
            <div className="mx-auto max-w-3xl space-y-4">
                <div>
                    <h1 className="text-3xl font-bold">{mode === 'status' ? 'Check My Application' : 'Apply to Drive'}</h1>
                    <p className={dark ? 'text-gray-400' : 'text-gray-600'}>Driver-app candidate portal for your current invite.</p>
                </div>

                {message && <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">{message}</div>}

                {applicant && (
                    <section className={`rounded-xl border p-5 space-y-4 ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-xl font-bold">{applicant.candidate_name}</h2>
                                <p className={dark ? 'text-gray-400' : 'text-gray-600'}>{applicant.candidate_email} · {formatPhone(applicant.candidate_mobile, 'US')}</p>
                            </div>
                            <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700">{statusLabel(applicant.status)}</span>
                        </div>

                        <div>
                            <h3 className="font-semibold">Selected onboarding steps</h3>
                            {steps.length === 0 ? (
                                <p className="text-sm text-gray-500">No driver onboarding steps were selected on this invite.</p>
                            ) : (
                                <ul className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                                    {steps.map((step) => <li key={step.id} className="rounded-lg border px-3 py-2 text-sm">{step.label}</li>)}
                                </ul>
                            )}
                        </div>

                        {mode === 'apply' && !applicant.application_started_at && (
                            <div className="space-y-3 rounded-lg border border-dashed p-4">
                                <h3 className="font-semibold">Validate your phone before starting</h3>
                                <button type="button" onClick={sendCode} disabled={working} className="rounded-lg bg-orange-600 px-4 py-2 font-medium text-white disabled:opacity-50">Send SMS code</button>
                                {codeSent && (
                                    <div className="flex flex-col gap-2 md:flex-row">
                                        <input type="text" value={code} onChange={(event) => setCode(event.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6-digit code" className="rounded-lg border px-3 py-2" />
                                        <button type="button" onClick={verifyCode} disabled={working || !code} className="rounded-lg border px-4 py-2 font-medium">Verify code</button>
                                    </div>
                                )}
                                <button type="button" onClick={startApplication} disabled={working || !validationToken} className="rounded-lg bg-green-600 px-4 py-2 font-medium text-white disabled:opacity-50">Start application</button>
                            </div>
                        )}

                        <div className="text-sm text-gray-600">
                            <p>Application started: {applicant.application_started_at ? new Date(applicant.application_started_at).toLocaleString() : 'Not started yet'}</p>
                            <p>Onboarding record: {onboardingRecord?.status ? statusLabel(onboardingRecord.status) : 'Not created yet'}</p>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

export default DriverApplicationPortal;
